/**
 * analytics.ts
 *
 * Thin analytics wrapper for access-landing.
 * In production: routes events to GrowthBook track API.
 * In test/dev environments without GrowthBook initialized: no-ops cleanly.
 *
 * Privacy invariant: no PII in any event property.
 * CI lint fails if console.log() is used here — use only GrowthBook API or no-op.
 */

/**
 * Track an analytics event.
 * Properties must be string key-value pairs only — no PII, no complex objects.
 *
 * @param name - Event name (e.g. 'discovery_page_viewed', 'provider_link_shared')
 * @param properties - Optional string key-value properties (no PII)
 */
export function trackEvent(name: string, properties?: Record<string, string>): void {
  // GrowthBook browser SDK is not loaded in access-landing (it's a public page, not a portal).
  // We emit events using GrowthBook's data-layer-compatible format.
  // In production, these events are captured by the GrowthBook analytics pipeline.
  // In test environments or when GrowthBook is not initialized: no-op silently.

  try {
    // Attempt GrowthBook track via data layer if available
    const win = window as unknown as {
      dataLayer?: Array<Record<string, unknown>>;
      growthbook?: { track?: (event: string, props?: Record<string, string>) => void };
    };

    if (win.growthbook?.track !== undefined) {
      win.growthbook.track(name, properties);
      return;
    }

    // GrowthBook data layer push (GTM-compatible)
    if (Array.isArray(win.dataLayer)) {
      win.dataLayer.push({
        event: name,
        ...properties,
      });
      return;
    }

    // No analytics infrastructure available — silent no-op (test env or pre-init)
    // DO NOT use console.log here (validate-phi-logging CI gate will fail)
  } catch {
    // Silently swallow errors — analytics failures must never break the user experience
  }
}
